import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { UserContext } from '../../../../components/context/userContext';

import loadable from '@loadable/component';
import PremiumPaywall from '../../../../components/PremiumPaywall/PremiumPaywall';

const NotFound = loadable(() =>
  import('../../../../components/not_found_component/not_found_component')
);
const AdContainer = loadable(() =>
  import('../../../../components/ad/AdContainer')
);
const Section = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.Section
  }
);
const Image = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.Image
  }
);
const Text = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.Text
  }
);
const MultiTabList = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.MultiTabList
  }
);
const FranchiseTopper = loadable(
  () => import('../../../../fc-components/main/components'),
  {
    resolveComponent: components => components.FranchiseTopper
  }
);
const StickyBar = loadable(
  () => import('../../../../components/StickyBar/StickyBar'),
  {
    resolveComponent: components => components.StickyBar
  }
);

const FranchisePage = ({ franchisePage }) => {
  const { hasPaywallAccess } = useContext(UserContext);
  const [isMobile, setIsMobile] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [tabLists, setTabLists] = useState([]);
  const [readMore, setReadMore] = useState(false);
  const [introScrollBack, setIntroScrollBack] = useState(null);

  const checkIfMobile = x => {
    setIsMobile(x.matches);
  };

  const dataArr = [];
  const theData = get(franchisePage, 'data.externalList', []);
  dataArr.push(theData.Winners, theData.Honorees);

  // Fields to map to airtable keys (Make sure airtable keys match with Table component this data is fed to)
  const winnersFields = [
    { label: 'icon', name: 'icon', weight: '1', width: '50' },
    { label: 'title', name: 'titles', weight: '1', width: '200' },
    { label: 'description', name: 'description', weight: '1', width: '253' },
    { label: 'image', name: 'image', weight: '1', width: '350' }
  ];

  const honoreesFields = [
    { label: 'icon', name: 'icon', weight: '1', width: '50' },
    { label: 'title', name: 'titles', weight: '1', width: '' },
    { label: 'description', name: 'description', weight: '0', width: '' }
  ];

  const winnersData = get(dataArr, '[0]', []);
  const honoreesData = get(dataArr, '[1]', []);

  let winnersDataFields = winnersData;
  let honoreesDataFields = honoreesData;

  if (!hasPaywallAccess()) {
    winnersDataFields = winnersData.slice(0, 2);
    honoreesDataFields = honoreesData.slice(0, 2);
  }

  dataArr.forEach((tab, idx) => {
    /* eslint-disable no-param-reassign */
    tab.behavior = 'link';
    tab.category = idx === 0 ? 'Winners' : 'Honorees';
    tab.data = idx === 0 ? winnersDataFields : honoreesDataFields;
    tab.fields = idx === 0 ? winnersFields : honoreesFields;
    tab.image = false;
    tab.type = 'company_list';
    tab.index = idx;
  });

  // Detect screen size on render, and watch for changes to it.
  useEffect(
    () => {
      const x = window.matchMedia('(max-width: 640px)');
      checkIfMobile(x);
      x.addListener(checkIfMobile);
      // eslint-disable-next-line
    const fullPackageData = get('franchisePage', 'data.acf', null);
      const query = window.location.search.replace('?', '').split('=');

      if (query[0] === 'view') setActiveTab(parseInt(query[1], 8));
      // setTabLists(olddata)
      setTabLists(dataArr);
    },
    [hasPaywallAccess()]
  );

  useEffect(
    () => {
      if (isMobile) setActiveTab(0);
    },
    [isMobile]
  );

  const fullPackageData = get(franchisePage, 'data.acf', null);
  const year = get(fullPackageData, 'franchise_year', 2021);
  const pageTitle = get(franchisePage, 'data.title.rendered', '');
  const seoSubHeadline = get(franchisePage, 'data.excerpt.rendered', '');
  const franchiseContributors = get(
    franchisePage,
    'data.acf.franchiseContributors',
    []
  );
  const featureImageDesktop = get(
    fullPackageData,
    'franchise_images.franchise_hero_image_desktop'
  );
  const featureImageMobile = get(
    fullPackageData,
    'franchise_images.franchise_hero_image_mobile'
  );
  const blurbImage = get(
    fullPackageData,
    'franchise_images.franchise_blurb_image'
  );
  const franchiseHeroImageCaption = get(
    fullPackageData,
    'franchise_images.franchise_hero_image_caption',
    'no caption'
  );
  const franchiseBlurb = get(fullPackageData, 'franchise_blurb', '');
  const dangerouslySlicedDescription = franchiseBlurb;
  let newDangerouslySlicedDescription = null;
  let readFlag = true;
  const slicedDescription = dangerouslySlicedDescription.replace(
    /(<([^>]+)>)/gi,
    ''
  );

  if (slicedDescription && slicedDescription.length > 746) {
    readFlag = false;
    const difference =
      dangerouslySlicedDescription.length - slicedDescription.length;
    newDangerouslySlicedDescription = dangerouslySlicedDescription.slice(
      0,
      664 + difference
    );
  }

  // Handle the Advertising Data
  const interstitialFreq = 10;
  const interstitial = (
    <React.Fragment>
      <AdContainer type="break" />
    </React.Fragment>
  );

  const goToTopOfList = () => {
    const distance =
      window.pageYOffset +
      document.querySelector('.tableSection').getBoundingClientRect().top;
    window.scrollTo({ top: distance - 60, left: 0, behavior: 'smooth' });
  };

  if (
    franchisePage.error ||
    // eslint-disable-next-line
    // status === 404 ||
    isEmpty(franchisePage) ||
    year < 2012 ||
    !year
  ) {
    return (
      <section className="ibd-page">
        <article className="ibd-page__main--error-pg">
          <NotFound />
        </article>
      </section>
    );
  }

  return (
    <div className="ibd2022" id="editorialLander">
      <Section
        bdColor="transparent"
        name="franchise-headline"
        maxWidth={970}
        // noPadding
      >
        <FranchiseTopper headline={pageTitle} subHeadline={seoSubHeadline} />
      </Section>

      <div className="heroSection">
        <div className="desktopImage">
          <Section bgColor="#f7f7f7" maxWidth={1110}>
            <div className="desktopHeroImage">
              <Image
                image={featureImageDesktop}
                caption={franchiseHeroImageCaption}
              />
            </div>
            <div className="mobileHeroImage">
              <Image
                image={featureImageMobile}
                caption={franchiseHeroImageCaption}
              />
            </div>
          </Section>
        </div>
      </div>
      <svg
        id="vector1"
        width="211"
        height="123"
        viewBox="0 0 211 123"
        fill="none"
        xmlns="https://www.w3.org/2000/svg"
      >
        <path
          d="M25.445 122.36C9.315 122.36 -18.705 112.515 -39 102.795L-35.545 95.5801C-20.05 103 -4.15 108.825 9.235 111.99C21.57 114.905 27.69 114.58 29.865 114.01C29.14 112.87 27.11 110.44 21.525 106.47C10.27 98.4801 1.11 90.3501 -4.975 82.9551C-12.05 74.3601 -14.46 67.3101 -12.135 62.0101C-8.985 54.8101 1.165 53.1301 18.885 56.8651C21.145 57.2601 23.44 57.6851 25.765 58.1251C25.395 57.2551 25.04 56.3801 24.695 55.5001C17.46 37.0801 16.865 20.7501 23.06 10.6901C29.865 -0.359861 46.95 -8.46986 89.7 15.6351C147.3 48.1101 178.555 63.7951 204.3 36.7151L210.1 42.2251C178.86 75.0851 139.815 53.0701 85.77 22.6001C56.73 6.23514 36.88 3.49514 29.865 14.8851C25.145 22.5501 26.015 36.9901 32.135 52.5751C33.14 55.1301 34.25 57.6451 35.46 60.0901C62.76 65.9651 90.725 75.0401 97.12 88.2101C98.96 91.9951 98.915 95.8801 97 99.4501L96.805 99.7801C93.93 104.09 90.425 106.605 86.085 107.465C78.475 108.98 68.51 105.375 54.71 96.1301C45.51 89.9651 36.745 79.5451 30.075 67.1301C26.125 66.3201 21.915 65.5201 17.43 64.7351L17.295 64.7101C1.485 61.3701 -4.04 63.4601 -4.81 65.2251C-6.525 69.1451 2.13 82.8901 26.155 99.9501C30.365 102.94 41.555 110.885 37.35 118.03C35.555 121.075 31.28 122.365 25.445 122.365V122.36ZM40.735 69.4651C46.18 78.0001 52.645 85.1101 59.165 89.4801C70.755 97.2451 79.285 100.65 84.525 99.6151C86.715 99.1801 88.37 97.9501 90.03 95.5101C90.635 94.2851 90.6 93.1001 89.92 91.7001C88.405 88.5851 80.545 78.8401 40.73 69.4601L40.735 69.4651Z"
          fill="black"
        />
      </svg>

      <svg
        id="vector2"
        width="139"
        height="184"
        viewBox="0 0 139 184"
        fill="none"
        xmlns="https://www.w3.org/2000/svg"
      >
        <path
          d="M29.545 183.39C16.245 175.71 0 157.52 0 147.235C0 144.195 1.32 141.76 3.72 140.375C7.06 138.45 13.25 137.6 31.675 148.24C53.51 160.845 69.42 160.815 74.785 157.715C76.635 156.645 76.875 155.53 76.875 154.68C76.875 151 71.74 143.73 57.27 135.375C14.855 110.88 4.105 98.055 13.035 82.59C21.315 68.25 47.795 81.95 76.53 98.54C93.05 108.08 106.555 108.97 112.115 105.765C114.215 104.55 114.46 103.095 114.46 102.1C114.46 100.37 113.23 91.02 88.965 77.01C83.365 73.775 78.015 70.205 79.385 65.24C80.94 59.615 88.77 59.73 101.74 59.93C106.375 60 114.225 60.12 118.22 59.455C116.565 57.91 113.05 55.205 105.635 50.92C89.855 41.81 84.225 35.945 85.545 30.01C87.18 22.655 98.03 21.72 111.765 20.53C117.34 20.05 126.3 19.275 129.365 17.85C128.34 16.495 125.12 13.33 115.1 7.545L119.45 0C134.315 8.58 139.61 14.16 138.335 19.895C136.735 27.105 126.045 28.03 112.51 29.2C107.105 29.67 97.44 30.505 94.425 32.035C95.37 33.4 98.66 36.84 109.985 43.38C124.615 51.825 129.51 56.94 128.045 62.24C126.175 69.01 115.865 68.85 101.605 68.63C98.695 68.585 94.96 68.53 91.895 68.625C92.325 68.885 92.795 69.165 93.315 69.465C112.565 80.58 123.165 92.165 123.165 102.095C123.165 106.825 120.785 110.805 116.465 113.3C106.815 118.875 89.43 116.04 72.175 106.075C60.38 99.265 48.06 92.425 37.905 88.515C28.56 84.92 22.085 84.33 20.575 86.94C16.505 93.99 16.785 101.935 61.625 127.825C77.3 136.875 85.585 146.155 85.585 154.67C85.585 159.09 83.295 162.845 79.14 165.245C68.385 171.45 48.045 167.735 27.325 155.77C15.43 148.9 10.455 147.955 8.73 147.9C9.5 152.74 20.4 168.05 33.9 175.84L29.545 183.38V183.39Z"
          fill="black"
        />
      </svg>

      <svg
        id="vector3"
        width="129"
        height="156"
        viewBox="0 0 129 156"
        fill="none"
        xmlns="https://www.w3.org/2000/svg"
      >
        <path
          id="Vector"
          d="M31.29 155.595C24.725 155.595 18.015 152.69 13.415 148.32C6.205 141.475 2.765 131.725 0 123.895L8.485 120.9C11.055 128.18 13.97 136.435 19.61 141.795C24.255 146.2 32.395 148.55 37.445 144.655C42.735 140.575 42.815 131.955 42.895 123.62L42.915 121.62C43.1 107.955 47.325 98.2 55.13 93.41C62.865 88.665 73.885 89.47 83.205 95.46C89.03 99.205 93.595 104.22 98.005 109.07C99.255 110.445 100.505 111.82 101.78 113.16C107.385 119.045 112.32 122.83 117.305 125.07C123.305 127.765 129.6 128.14 134.58 126.1C139.81 123.96 143.42 118.835 143.165 113.92C142.985 110.395 140.875 106.61 137.375 103.54C133.845 100.44 129.29 98.215 124.885 96.06L90.1 79.075C85.91 77.03 76.105 72.235 75.68 62.785C75.32 54.76 82.12 49.01 88.61 46.305C106.095 39.01 127.745 45.02 138.97 60.29C141.735 64.05 143.775 68.125 145.745 72.07C147.45 75.48 149.065 78.705 151.07 81.625C157.105 90.425 167.04 96.425 177.635 97.675C185 98.535 193.63 96.155 195.86 89.91C197.98 83.975 193.405 77.045 187.795 73.265C181.93 69.315 174.885 67.385 167.425 65.34C166.055 64.965 164.69 64.59 163.335 64.205C154.25 61.615 144.045 57.87 137.25 49.715C130.185 41.24 128.47 26.675 137.18 18.505C146.27 9.98 160.83 13.05 169.915 20.27C174.615 24.005 178.335 28.545 181.935 32.94C184.64 36.24 187.195 39.36 190.095 42.1C195.98 47.66 204.885 51.665 211.51 48.08C218.25 44.43 219.3 34.035 216.025 26.985C212.265 18.895 204.535 13.11 196.27 7.415L201.375 0C210.33 6.175 219.47 13.05 224.185 23.185C226.725 28.65 227.48 35.185 226.25 41.115C224.865 47.805 221.15 53.09 215.795 55.99C208.725 59.815 196.135 60.185 183.915 48.635C180.6 45.5 177.74 42.015 174.975 38.64C171.52 34.43 168.26 30.45 164.315 27.315C158.095 22.37 148.545 20.175 143.335 25.07C138.405 29.695 139.815 38.735 144.16 43.955C149.39 50.23 158.035 53.34 165.8 55.55C167.13 55.93 168.465 56.295 169.805 56.66C177.635 58.805 185.73 61.025 192.825 65.8C201.06 71.345 208.175 82.2 204.34 92.935C200.355 104.105 187.15 107.85 176.585 106.61C163.445 105.065 151.13 97.625 143.645 86.715C141.29 83.28 139.46 79.63 137.695 76.095C135.81 72.325 134.025 68.76 131.715 65.62C123.025 53.795 105.61 48.96 92.07 54.61C88.28 56.19 84.525 59.205 84.67 62.38C84.835 66.07 90.105 69.06 94.05 70.99L128.83 87.975C133.54 90.275 138.87 92.88 143.31 96.775C148.7 101.51 151.84 107.43 152.15 113.455C152.6 122.2 146.775 130.825 137.985 134.425C130.74 137.395 121.855 136.975 113.615 133.28C105.62 129.69 99.31 123.62 95.26 119.37C93.935 117.98 92.64 116.555 91.345 115.13C87.14 110.51 83.17 106.145 78.335 103.035C73.45 99.895 65.675 97.505 59.835 101.085C53.465 104.995 52.01 114.48 51.915 121.745L51.895 123.705C51.805 133.21 51.695 145.035 42.945 151.785C39.495 154.445 35.42 155.595 31.295 155.595H31.29Z"
          fill="black"
        />
      </svg>

      <svg
        id="vector4"
        width="170"
        height="115"
        viewBox="0 0 170 115"
        fill="none"
        xmlns="https://www.w3.org/2000/svg"
      >
        <path
          d="M66.96 114.29C58.475 114.29 51.89 111.445 47.25 108.37C41.255 104.395 36.14 98.4102 32.215 91.1102C29.305 92.5252 26.3 93.1352 23.36 92.9952C16.75 92.6802 10.775 88.7402 6.525 81.9052C2.255 75.0352 0 65.5902 0 54.5952V50.0952H9V54.5952C9 73.4402 16.44 83.6552 23.79 84.0102C25.325 84.0902 26.925 83.7302 28.52 82.9252C25.325 74.3952 23.555 64.6902 23.555 54.5952C23.555 38.3052 29.175 23.0102 36.63 19.0202C39.45 17.5102 42.505 17.6002 45.01 19.2652C50.805 23.1202 52.68 34.8202 50.745 55.0202C49.36 69.4702 44.99 79.4902 39.42 85.5952C42.72 92.1502 47.08 97.4552 52.225 100.865C60.52 106.365 70.405 106.75 81.6 102.015C89.335 98.7452 94.525 92.7152 97.06 84.0652C89.03 78.4352 81.34 71.9202 75.365 65.5502C51.03 39.6402 51.29 13.0302 60.835 3.78518C68.285 -3.43482 77.78 0.860177 83.65 6.72518C98.16 21.2352 110.9 55.0802 107.145 79.9702C121.975 89.4252 138.2 95.6852 148.53 91.5952C156.635 88.3852 160.745 78.5902 160.745 62.4702V57.9702H169.745V62.4702C169.745 82.6452 163.725 95.2602 151.845 99.9602C138.895 105.08 122.475 98.8352 110.975 92.6852C108.955 91.6052 106.905 90.4252 104.85 89.1702C101.27 99.0752 94.54 106.31 85.105 110.3C78.325 113.17 72.27 114.285 66.96 114.285V114.29ZM40.355 27.2702C37.66 29.4152 32.55 39.7452 32.55 54.5902C32.55 62.0852 33.615 69.3052 35.565 75.8252C38.525 70.9452 40.86 63.7952 41.78 54.1652C43.6 35.1852 41.295 28.9552 40.35 27.2702H40.355ZM70.05 9.01018C69.015 9.01018 68.005 9.36518 67.095 10.2452C61.21 15.9502 61.13 37.2452 81.92 59.3902C86.465 64.2302 92.295 69.3802 98.715 74.1552C100.04 52.6752 89.155 24.9652 77.28 13.0902C75.545 11.3552 72.71 9.01518 70.045 9.01518L70.05 9.01018Z"
          fill="black"
        />
      </svg>

      <div className="introSection">
        <Section bgColor="#f7f7f7" maxWidth={810}>
          <div className="introText">
            <Text>
              <Image image={blurbImage} width="85px" float="left" />
              <div className="content">
                {!readMore &&
                  !readFlag && (
                    <div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: newDangerouslySlicedDescription
                        }}
                      />
                      <a
                        className="readMore"
                        onClick={() => {
                          setReadMore(true);
                          setIntroScrollBack(window.scrollY);
                        }}
                      >
                        {' '}
                        + Read more{' '}
                      </a>
                    </div>
                  )}
                {readMore && (
                  <div>
                    <div dangerouslySetInnerHTML={{ __html: franchiseBlurb }} />
                    <a
                      className="readMore"
                      onClick={() => {
                        setReadMore(false);
                        if (introScrollBack) {
                          window.scrollTo(window.scrollX, introScrollBack);
                        }
                      }}
                    >
                      {' '}
                      - Read Less{' '}
                    </a>
                  </div>
                )}
              </div>
            </Text>
          </div>
        </Section>
      </div>
      <div className="tableSection">
        <Section bgColor="#f7f7f7" maxWidth={1020} scrollHere noPadding>
          <MultiTabList
            list="ibd-2022"
            data={tabLists}
            interstitialUnit={interstitial}
            interstitialFreq={interstitialFreq}
            isMobile={isMobile}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          {!hasPaywallAccess() && <PremiumPaywall />}
        </Section>
      </div>
      <Section
        addClass="creditsSection"
        bgColor="transparent"
        name="franchise-credits"
        maxWidth={700}
        margin="40px auto 40px auto"
        noPadding
      >
        <span
          className="section__franchise-credits--content"
          dangerouslySetInnerHTML={{ __html: franchiseContributors }}
        />
      </Section>
      <StickyBar
        slug={get(franchisePage, 'data.slug', 'default')}
        externalData={dataArr} // Category data coming from Airtable not WP
        lists={tabLists}
        location="lander"
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isMobile={isMobile}
        post={{
          id: 'innovation-by-design',
          slug: get(franchisePage, 'data.slug', 'ibd-2022-2'),
          title: get(fullPackageData, 'title', 'Fast Company')
        }}
        scroll={goToTopOfList}
      />
    </div>
  );
};

FranchisePage.propTypes = {
  franchisePage: PropTypes.shape({}).isRequired
};

export default FranchisePage;
